import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import api from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Mail, ShieldCheck } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

interface InvitationPreview {
  email: string
  role: string
  org_id: string
  org_name: string | null
  inviter_name: string | null
  inviter_email: string | null
  user_exists: boolean
  expires_at: string | null
}

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { setTokens } = useAuth() as any

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null)

  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [accepting, setAccepting] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const res = await api.get<InvitationPreview>(`/invitations/${token}`)
        if (!cancelled) setInvitation(res.data)
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || 'This invitation could not be loaded.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (token) load()
    return () => { cancelled = true }
  }, [token])

  const handleAccept = async () => {
    setAccepting(true)
    setAcceptError(null)
    try {
      const body: Record<string, unknown> = {}
      if (!invitation?.user_exists) {
        body.full_name = fullName
        body.password = password
      } else if (password) {
        body.password = password
      }
      const res = await api.post<{ access_token: string; refresh_token: string }>(
        `/invitations/${token}/accept`,
        body,
      )
      // Persist tokens and route to dashboard
      localStorage.setItem('access_token', res.data.access_token)
      localStorage.setItem('refresh_token', res.data.refresh_token)
      if (typeof setTokens === 'function') {
        try { setTokens(res.data.access_token, res.data.refresh_token) } catch {}
      }
      navigate('/dashboard', { replace: true })
    } catch (e: any) {
      setAcceptError(e?.response?.data?.detail || 'Failed to accept invitation.')
    } finally {
      setAccepting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <Mail className="h-10 w-10 text-muted-foreground mx-auto" />
            <h1 className="text-lg font-semibold">Invitation not available</h1>
            <p className="text-sm text-muted-foreground">{error || 'This invitation link is invalid or has expired.'}</p>
            <Button asChild variant="outline">
              <Link to="/login">Go to login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const isNewUser = !invitation.user_exists
  const canSubmit = isNewUser
    ? fullName.trim().length > 0 && password.length >= 6
    : true

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mx-auto mb-3">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle>You've been invited</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm">
            <p className="text-blue-900">
              <span className="font-medium">
                {invitation.inviter_name || invitation.inviter_email || 'Someone'}
              </span>
              {' has invited '}
              <span className="font-medium">{invitation.email}</span>
              {' to join '}
              <span className="font-medium">{invitation.org_name || 'an organisation'}</span>
              {' as '}
              <span className="font-medium">{invitation.role.replace('org_', '')}</span>.
            </p>
          </div>

          {isNewUser ? (
            <>
              <p className="text-sm text-muted-foreground">
                Create your account to accept this invitation.
              </p>
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  autoFocus
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              An account with <span className="font-medium">{invitation.email}</span> already exists.
              Click Accept to join {invitation.org_name || 'this organisation'}.
            </p>
          )}

          {acceptError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{acceptError}</div>
          )}

          <Button onClick={handleAccept} disabled={!canSubmit || accepting} className="w-full">
            {accepting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Accepting...</>
            ) : isNewUser ? (
              'Create account & join'
            ) : (
              `Accept & join ${invitation.org_name || 'organisation'}`
            )}
          </Button>

          {!isNewUser && (
            <p className="text-xs text-muted-foreground text-center">
              Already logged in with a different account?{' '}
              <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
              {' '}with {invitation.email} first.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
